// v5.2.0 · Tecnología → Estado y Configuración de la videollamada (Agora).
// mode="estado": semáforo + prueba de conexión real. mode="configuracion": ajustes editables + credenciales (solo estado).
import HostUsageCard from "@/components/HostUsageCard";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Gauge, Activity, CheckCircle2, XCircle, AlertTriangle, Loader2, Radio, Video, Save, RefreshCw, SlidersHorizontal } from "lucide-react";

const OVERALL = {
  ok: { label: "Videollamada operativa", color: "#05A859", bg: "#D8F1E4" },
  warning: { label: "Operativa con advertencias", color: "#C98A00", bg: "#FFF1CC" },
  down: { label: "Videollamada con problemas", color: "#D64545", bg: "#FBDDDD" },
};

function Check({ c }) {
  const Icon = c.ok ? CheckCircle2 : c.warn ? AlertTriangle : XCircle;
  const color = c.ok ? "#05A859" : c.warn ? "#C98A00" : "#D64545";
  return (
    <li className="flex items-start gap-3 py-2.5" data-testid={`video-check-${c.id}`}>
      <Icon size={18} color={color} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-bold text-[#1F2547]">{c.label}</p>
        <p className="text-xs text-[#6E7591]">{c.detail}</p>
      </div>
    </li>
  );
}

const LEVEL = { ok: "#05A859", warning: "#C98A00", danger: "#D64545" };
const FIELD_LABEL = { durationAudioAll: "Audio", durationVideoHd: "Video HD", durationVideoHdp: "Video HD+/Full HD", durationVideo1080P: "Video 1080p", durationVideo2K: "Video 2K", durationVideo4K: "Video 4K" };
const fmt = (n) => Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });

// v5.3.0 · Consumo real de minutos del plan de Agora (minutos estándar: audio ×1 · video HD ×4 · Full HD ×9).
function UsagePanel() {
  const [u, setU] = useState(null);
  const [month, setMonth] = useState("");
  const [busy, setBusy] = useState(true);
  const load = useCallback(async (m) => {
    setBusy(true);
    try {
      const { data } = await api.get("/video/usage", { params: m ? { month: m } : {} });
      setU(data); setMonth(data.month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo consultar el consumo");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(""); }, [load]);
  if (!u) return busy ? <p className="text-sm text-[#6E7591]"><Loader2 size={14} className="inline animate-spin mr-1" /> Calculando consumo…</p> : null;
  const color = LEVEL[u.level];
  const days = (u.official.available ? u.official.by_day : u.local.by_day) || [];
  const maxDay = Math.max(1, ...days.map((d) => d.std));
  const f = u.official.available ? u.official.fields : null;
  return (
    <div className="neo-card p-5 space-y-5" data-testid="video-usage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-brand text-lg text-[#1F2547] inline-flex items-center gap-2"><Gauge size={18} color="#EF7015" /> Consumo de minutos del plan</h2>
        <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
          data-testid="usage-month" className="px-3 py-1.5 text-sm font-bold text-[#1F2547]" />
      </div>

      <div>
        <div className="flex items-end justify-between">
          <p className="font-brand text-4xl" style={{ color }} data-testid="usage-used">{fmt(u.used_standard_minutes)}
            <span className="text-base text-[#6E7591]"> / {fmt(u.quota_minutes)} min estándar</span></p>
          <p className="text-sm font-extrabold" style={{ color }}>{u.percent}%</p>
        </div>
        <div className="h-3 rounded-full bg-[#D3D9E6] overflow-hidden mt-2">
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, u.percent)}%`, background: color }} />
        </div>
        <p className="text-xs text-[#6E7591] mt-2">
          Fuente: {u.source === "agora" ? "Agora (dato oficial)" : "estimación propia (no hay acceso a la API de uso de Agora)"} ·
          Proyección a fin de mes: <b>{fmt(u.projected_standard_minutes)}</b> min ({u.projected_percent}% del plan).
          {u.projected_percent >= 100 && <b style={{ color: LEVEL.danger }}> Se agotaría antes de terminar el mes.</b>}
        </p>
        {!u.official.available && <p className="text-xs text-[#C98A00] mt-1" data-testid="usage-official-reason">Dato oficial no disponible: {u.official.reason}</p>}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl bg-white/70 p-3"><p className="text-[11px] font-extrabold uppercase text-[#6E7591]">Minutos de participantes</p>
          <p className="font-brand text-2xl text-[#315197]">{fmt(u.local.participant_minutes)}</p><p className="text-[11px] text-[#6E7591]">{u.local.sessions} entradas a sala (propio)</p></div>
        <div className="rounded-2xl bg-white/70 p-3"><p className="text-[11px] font-extrabold uppercase text-[#6E7591]">Con video / solo audio</p>
          <p className="font-brand text-2xl text-[#A43790]">{fmt(u.local.video_minutes)} <span className="text-sm text-[#6E7591]">/ {fmt(u.local.audio_minutes)}</span></p><p className="text-[11px] text-[#6E7591]">video cuenta ×{u.ratios.hd}, audio ×{u.ratios.audio}</p></div>
        <div className="rounded-2xl bg-white/70 p-3"><p className="text-[11px] font-extrabold uppercase text-[#6E7591]">Grabación en la nube</p>
          <p className="font-brand text-2xl text-[#05A859]">{u.official.available ? fmt(u.official.recording.minutes) : "—"}</p><p className="text-[11px] text-[#6E7591]">min (se factura aparte)</p></div>
      </div>

      {f && (
        <div data-testid="usage-fields">
          <p className="text-xs font-extrabold uppercase text-[#6E7591] mb-1">Detalle oficial por tipo (minutos reales)</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(f).map(([k, v]) => (
              <span key={k} className="px-3 py-1.5 rounded-xl bg-white/70 text-xs font-bold text-[#1F2547]">{FIELD_LABEL[k] || k}: {fmt(v)}</span>
            ))}
          </div>
          {(f.durationVideoHdp > 0 || f.durationVideo1080P > 0 || f.durationVideo2K > 0) && (
            <p className="text-xs mt-2" style={{ color: LEVEL.warning }}>Hubo minutos por encima de HD: esos minutos consumen mucho más del plan (×9 o más). Revisa que todos usen la versión v5.3.0.</p>
          )}
        </div>
      )}

      {days.length > 0 && (
        <div>
          <p className="text-xs font-extrabold uppercase text-[#6E7591] mb-2">Minutos estándar por día</p>
          <div className="flex items-end gap-1 h-24" data-testid="usage-days">
            {days.map((d) => (
              <div key={d.day} className="flex-1 min-w-[6px] rounded-t bg-[#315197]/80 hover:bg-[#EF7015]" title={`${d.day}: ${fmt(d.std)} min`}
                style={{ height: `${Math.max(4, (d.std / maxDay) * 100)}%` }} />
            ))}
          </div>
        </div>
      )}

      {u.local.top_classes.length > 0 && (
        <div data-testid="usage-top-classes">
          <p className="text-xs font-extrabold uppercase text-[#6E7591] mb-1">Clases que más consumen (estimado propio)</p>
          <ul className="divide-y divide-[#D3D9E6]">
            {u.local.top_classes.map((c) => (
              <li key={c.channel} className="py-2 flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate font-bold text-[#1F2547]">{c.title}</span>
                <span className="text-xs text-[#6E7591] shrink-0">{c.participants} entradas · {fmt(c.std)} min estándar</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <HostUsageCard month={month} />

      <p className="text-[11px] text-[#6E7591]">{u.cap} {u.local.note}</p>
    </div>
  );
}

export default function AdminVideollamada({ mode = "estado" }) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [form, setForm] = useState({ auto_record: true, token_ttl_minutes: 60, monthly_quota_min: 10000 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (live = false) => {
    live ? setProbing(true) : setLoading(true);
    try {
      const { data } = await api.get("/video/status", { params: { live } });
      setSt(data);
      setForm(data.settings);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo consultar el estado de la videollamada");
    } finally { setLoading(false); setProbing(false); }
  }, []);
  useEffect(() => { load(false); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/video/settings", form);
      setForm(data);
      toast.success("Configuración guardada. Aplica a las próximas salas.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const o = OVERALL[st?.overall] || OVERALL.ok;
  const isEstado = mode === "estado";

  return (
    <div className="space-y-5" data-testid={`video-${mode}`}>
      <div>
        <p className="text-[11px] tracking-[0.2em] uppercase font-extrabold text-[#EF7015]">Tecnología</p>
        <h1 className="font-brand text-3xl text-[#1F2547] inline-flex items-center gap-2">
          {isEstado ? <Activity className="text-[#EF7015]" size={28} /> : <SlidersHorizontal className="text-[#EF7015]" size={28} />}
          {isEstado ? "Estado de la videollamada" : "Configuración de la videollamada"}
        </h1>
        <p className="text-sm text-[#6E7591]">Proveedor: {st?.provider || "—"}{st?.app_id_preview ? ` · App ID ${st.app_id_preview}` : ""}</p>
      </div>

      {loading && <p className="text-sm text-[#6E7591]"><Loader2 size={14} className="inline animate-spin mr-1" /> Consultando…</p>}

      {st && isEstado && (
        <>
          <div className="neo-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid="video-overall">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-2xl grid place-items-center" style={{ background: o.bg }}><Video size={22} color={o.color} /></span>
              <div>
                <p className="font-brand text-xl" style={{ color: o.color }}>{o.label}</p>
                <p className="text-xs text-[#6E7591]">Revisado {new Date(st.checked_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}{st.probed ? " · con prueba de conexión real" : ""}</p>
              </div>
            </div>
            <button type="button" onClick={() => load(true)} disabled={probing} data-testid="video-probe-btn"
              className="jasq-btn-primary inline-flex items-center gap-2 text-sm">
              {probing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Probar conexión con Agora
            </button>
          </div>

          <UsagePanel />

          <div className="grid gap-5 lg:grid-cols-3">
            <div className="neo-card p-5 lg:col-span-2">
              <h2 className="font-brand text-lg text-[#1F2547] mb-1">Verificaciones</h2>
              <ul className="divide-y divide-[#D3D9E6]">{st.checks.map((c) => <Check key={c.id} c={c} />)}</ul>
            </div>
            <div className="space-y-5">
              <div className="neo-card p-5" data-testid="video-live-now">
                <p className="text-xs font-extrabold uppercase text-[#6E7591] flex items-center gap-1.5"><Radio size={13} /> Clases en vivo ahora</p>
                <p className="font-brand text-4xl text-[#05A859]">{st.live_now}</p>
              </div>
              <div className="neo-card p-5" data-testid="video-recordings">
                <p className="text-xs font-extrabold uppercase text-[#6E7591]">Grabaciones · últimos 7 días</p>
                <p className="font-brand text-4xl text-[#315197]">{st.recordings_7d.ready}<span className="text-base text-[#6E7591]"> / {st.recordings_7d.total}</span></p>
                <p className="text-[11px] text-[#6E7591]">listas / registradas{st.recordings_7d.classes_without_recording > 0 ? ` · ${st.recordings_7d.classes_without_recording} clase(s) sin grabación` : ""}</p>
              </div>
            </div>
          </div>
        </>
      )}

      {st && !isEstado && (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="neo-card p-5 space-y-5" data-testid="video-settings">
            <h2 className="font-brand text-lg text-[#1F2547]">Ajustes de las salas</h2>
            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <span>
                <span className="block text-sm font-bold text-[#1F2547]">Grabar las clases automáticamente</span>
                <span className="block text-xs text-[#6E7591]">Inicia la grabación en la nube cuando el docente entra a la sala.</span>
              </span>
              <input type="checkbox" checked={!!form.auto_record} onChange={(e) => setForm((f) => ({ ...f, auto_record: e.target.checked }))}
                data-testid="video-auto-record" className="w-5 h-5 accent-[#A43790]" />
            </label>
            <label className="block">
              <span className="block text-sm font-bold text-[#1F2547]">Vigencia del acceso a la sala (minutos)</span>
              <span className="block text-xs text-[#6E7591] mb-2">Entre 15 y 480. Si una clase dura más, la persona debe reingresar.</span>
              <input type="number" min={15} max={480} value={form.token_ttl_minutes}
                onChange={(e) => setForm((f) => ({ ...f, token_ttl_minutes: Number(e.target.value) }))}
                data-testid="video-token-ttl" className="w-40 px-3 py-2 font-bold text-[#1F2547]" />
            </label>
            <label className="block">
              <span className="block text-sm font-bold text-[#1F2547]">Minutos estándar del plan por mes</span>
              <span className="block text-xs text-[#6E7591] mb-2">Plan gratuito de Agora = 10 000. Cámbialo si compras un paquete; solo afecta el medidor de consumo.</span>
              <input type="number" min={1000} step={1000} value={form.monthly_quota_min}
                onChange={(e) => setForm((f) => ({ ...f, monthly_quota_min: Number(e.target.value) }))}
                data-testid="video-quota" className="w-40 px-3 py-2 font-bold text-[#1F2547]" />
            </label>
            <button type="button" onClick={save} disabled={saving} data-testid="video-save-btn" className="jasq-btn-primary inline-flex items-center gap-2 text-sm">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar
            </button>
          </div>
          <div className="neo-card p-5" data-testid="video-credentials">
            <h2 className="font-brand text-lg text-[#1F2547] mb-1">Credenciales</h2>
            <p className="text-xs text-[#6E7591] mb-2">Por seguridad se administran en el servidor (variables de entorno) y aquí solo se muestra su estado.</p>
            <ul className="divide-y divide-[#D3D9E6]">{st.checks.filter((c) => c.id !== "probe").map((c) => <Check key={c.id} c={c} />)}</ul>
          </div>
        </div>
      )}
    </div>
  );
}
